import { useState } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Header } from "@/components/navigation/Header"
import { Sidebar } from "@/components/navigation/Sidebar"
import { MobileNav } from "@/components/navigation/MobileNav"
import { useAuth } from "@/components/layout/AuthProvider"
import { GuestPopup } from "@/components/auth/GuestPopup"

export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, initialized, loading } = useAuth()
  const location = useLocation()

  // Show a minimal loading state while auth resolves (avoids layout flash)
  if (!initialized || loading) {
    return (
      <div style={{ minHeight: "100dvh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg className="w-8 h-8 animate-spin" viewBox="0 0 24 24" fill="none" style={{ color: "#ff4500" }}>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity=".25" />
          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
    )
  }

  return (
    <div style={{ minHeight: "100dvh", background: "var(--bg)" }}>
      <div className="sq-loading-bar" id="sq-loader" style={{ display: "none" }} />
      <Header user={user} onMenuToggle={() => setSidebarOpen(v => !v)} />
      <div className="flex" style={{ paddingTop: "var(--header-h)" }}>
        <aside className="hidden lg:flex lg:flex-col lg:fixed lg:w-[220px] lg:top-[48px] lg:bottom-0 app-sidebar overflow-hidden">
          <Sidebar user={user} onClose={() => {}} />
        </aside>
        <AnimatePresence>
          {sidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: .15 }}
                className="fixed inset-0 z-50 lg:hidden"
                style={{ background: "rgba(0,0,0,.7)" }}
                onClick={() => setSidebarOpen(false)}
              />
              <motion.aside
                initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 280 }}
                className="fixed inset-y-0 left-0 z-50 w-[260px] lg:hidden"
                style={{ background: "var(--bg)", borderRight: "1px solid var(--border)" }}
              >
                <div style={{ padding: "1rem", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
                      <rect width="48" height="48" rx="8" fill="#ff4500" />
                      <path d="M15 17.5C15 14.462 17.462 12 20.5 12H29C30.105 12 31 12.895 31 14C31 15.105 30.105 16 29 16H20.5C19.672 16 19 16.672 19 17.5C19 18.328 19.672 19 20.5 19H27.5C30.538 19 33 21.462 33 24.5C33 27.538 30.538 30 27.5 30H19C17.895 30 17 30.895 17 32C17 33.105 17.895 34 19 34H28.5C29.328 34 30 34.672 30 35.5C30 36.328 29.328 37 28.5 37H19C16.239 37 14 34.761 14 32C14 29.239 16.239 27 19 27H27.5C28.328 27 29 26.328 29 25.5C29 24.672 28.328 24 27.5 24H20.5C17.462 24 15 21.538 15 18.5V17.5Z" fill="white" />
                    </svg>
                    <span className="sq-logo text-xl">SQUIDS</span>
                  </div>
                  <button onClick={() => setSidebarOpen(false)} style={{ color: "var(--text3)" }} aria-label="Close">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <Sidebar user={user} onClose={() => setSidebarOpen(false)} />
              </motion.aside>
            </>
          )}
        </AnimatePresence>
        <main style={{ flex: 1, minWidth: 0 }} className="lg:ml-[220px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: .16, ease: [0.16, 1, 0.3, 1] }}
              style={{ minHeight: "calc(100dvh - 48px)" }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      <MobileNav user={user} />
      {/* Only show guest popup to non-authenticated users */}
      {!user && <GuestPopup />}
    </div>
  )
}
